// client/src/components/admin/AdminVatCreditRequestDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';

const AdminVatCreditRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const res = await axios.get(`/api/vat-credit-requests/${id}`);
        if (res.data && res.data.request) {
          setRequest(res.data.request);
          setBids(res.data.bids || []);
        } else {
          setError('Invalid response format from server');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VAT credit request:', err);
        setError('Failed to fetch request details');
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.put(`/api/vat-credit-requests/${id}/status`, { status: newStatus });

      setRequest({ ...request, status: newStatus });
      setSuccessMessage(`Request status updated to ${newStatus.replace('_', ' ')}`);

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update request status');
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDeadline = (dateString) => {
    const deadline = new Date(dateString);
    const now = new Date();
    const isPast = deadline < now;

    return (
      <span className={isPast ? 'admin-deadline-passed' : 'admin-deadline-active'}>
        {formatDate(dateString)}
        {isPast && ' (Expired)'}
      </span>
    );
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return 'N/A';
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };

  const getWinningBids = () => bids.filter(bid => bid.status === 'approved');

  if (loading) {
    return <div className="loading">Loading request details...</div>;
  }

  if (error && !request) {
    return <div className="error-message">{error}</div>;
  }

  if (!request) {
    return <div className="error-message">Request not found</div>;
  }

  const winningBids = getWinningBids();
  const requestCurrency = request.currency || 'EUR';
  const totalAllocated = winningBids.reduce((sum, bid) => sum + (Number(bid.allocatedAmount) || 0), 0);

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">VAT Credit Request Details #{request.requestId}</h2>
      </div>

      {error && <div className="alert-danger">{error}</div>}
      {successMessage && <div className="alert-success">{successMessage}</div>}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Request Information</h3>
          <span className={`status status-${request.status}`}>
            {request.status.replace('_', ' ')}
          </span>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Request ID</div>
            <div className="admin-detail-value">#{request.requestId}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">System ID</div>
            <div className="admin-detail-value">#{request._id || request.id}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Company</div>
            <div className="admin-detail-value">
              {request.company ? (
                <Link to={`/admin/organizations/${request.company._id || request.company.id}`}>
                  {request.company.name}
                </Link>
              ) : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Currency</div>
            <div className="admin-detail-value">{requestCurrency}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Amount</div>
            <div className="admin-detail-value">{formatCurrency(request.amount, requestCurrency)}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Indicative Days</div>
            <div className="admin-detail-value">{request.indicativeDays}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Bid Deadline</div>
            <div className="admin-detail-value">
              {formatDeadline(request.bidDeadline)}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Execution Date</div>
            <div className="admin-detail-value">{formatDate(request.executionDate)}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Selected Banks</div>
            <div className="admin-detail-value">
              {(request.selectedBanks || []).length} bank(s)
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Created By</div>
            <div className="admin-detail-value">
              {request.createdBy ? `${request.createdBy.firstName} ${request.createdBy.lastName}` : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Created Date</div>
            <div className="admin-detail-value">{formatDate(request.createdAt)}</div>
          </div>

          {request.notes && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Notes</div>
              <div className="admin-detail-value">{request.notes}</div>
            </div>
          )}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Attachments</h3>
        </div>
        <div className="admin-card-body">
          <VatCreditAttachmentList
            requestId={request._id || request.id}
            attachments={request.attachments}
          />
        </div>
      </div>

      {request.status === 'completed' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Request Summary</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-summary-box">
              <div className="admin-summary-row">
                <div className="admin-summary-label">Requested Amount:</div>
                <div className="admin-summary-value">{formatCurrency(request.amount, requestCurrency)}</div>
              </div>

              <div className="admin-summary-row">
                <div className="admin-summary-label">Total Allocated:</div>
                <div className="admin-summary-value">{formatCurrency(totalAllocated, requestCurrency)}</div>
              </div>

              <div className="admin-summary-row">
                <div className="admin-summary-label">Winning Bids:</div>
                <div className="admin-summary-value">{winningBids.length}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Bids ({bids.length})</h3>
        </div>
        <div className="admin-card-body">
          {bids.length === 0 ? (
            <div className="admin-no-data-message">No bids have been submitted yet.</div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Bid ID</th>
                    <th>Bank</th>
                    <th>Desired Amount</th>
                    <th>Interest Rate</th>
                    <th>Commission</th>
                    <th>Official Days</th>
                    <th>Notification</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map(bid => (
                    <tr key={bid.id || bid._id}>
                      <td>#{bid.id || bid._id}</td>
                      <td>{bid.bank?.name || 'N/A'}</td>
                      <td>{formatCurrency(bid.desiredAmount, requestCurrency)}</td>
                      <td>
                        {bid.rateType === 'floating'
                          ? `${bid.baseRate ? bid.baseRate.replace('_', ' ').toUpperCase() + ' + ' : ''}${bid.interestRate || 0}%`
                          : `${bid.interestRate || 0}%`}
                      </td>
                      <td>{bid.commission != null ? `${bid.commission}%` : 'N/A'}</td>
                      <td>{bid.indicativeDays}</td>
                      <td>{bid.transferNotification === 'notified' ? 'Notified' : 'Not Notified'}</td>
                      <td>
                        <span className={`status status-${bid.status === 'approved' ? 'approved' : bid.status === 'rejected' ? 'rejected' : bid.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
                          {bid.status || 'pending'}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/vat-credit-requests/${request._id || request.id}/bids/${bid.id || bid._id}`}
                          className="admin-view-btn"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {request.status === 'completed' && winningBids.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Winning Bids</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Bid ID</th>
                    <th>Bank</th>
                    <th>Interest Rate</th>
                    <th>Allocated Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {winningBids.map(bid => (
                    <tr key={`winning-${bid.id || bid._id}`}>
                      <td>#{bid.id || bid._id}</td>
                      <td>{bid.bank?.name || 'N/A'}</td>
                      <td>
                        {bid.rateType === 'floating'
                          ? `${bid.baseRate ? bid.baseRate.replace('_', ' ').toUpperCase() + ' + ' : ''}${bid.interestRate || 0}%`
                          : `${bid.interestRate || 0}%`}
                      </td>
                      <td>{formatCurrency(bid.allocatedAmount, requestCurrency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Actions</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-actions">
            {request.status === 'bidding' && (
              <button
                className="btn btn-primary"
                onClick={() => handleStatusUpdate('pending_allocation')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Close Bidding'}
              </button>
            )}

            {(request.status === 'bidding' || request.status === 'pending_allocation') && (
              <button
                className="btn btn-danger"
                onClick={() => handleStatusUpdate('cancelled')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Cancel Request'}
              </button>
            )}

            {request.status === 'pending_allocation' && (
              <div className="admin-no-data-message" style={{ marginTop: '10px' }}>
                Fund allocation across bids is performed by the company from their portal.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVatCreditRequestDetails;
